import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { AlertTriangle, Loader2 } from "lucide-react";

interface Flag {
  key: string;
  enabled: boolean;
  description: string | null;
  updated_at: string;
}

const ORDER = ["pause_transactions", "disable_withdrawals", "disable_rewards", "disable_referrals", "disable_spin"];
const LABELS: Record<string, string> = {
  pause_transactions: "Pause peer-to-peer transactions",
  disable_withdrawals: "Disable new withdrawals",
  disable_rewards: "Disable signup reward",
  disable_referrals: "Disable referral qualification",
  disable_spin: "Disable spin wheel",
};

export default function AdminSystem() {
  const [flags, setFlags] = useState<Flag[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("system_flags").select("*");
    if (error) toast({ title: "Failed to load flags", description: error.message, variant: "destructive" });
    setFlags((data ?? []) as Flag[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggle = async (key: string, enabled: boolean) => {
    setSaving(key);
    const { error } = await supabase.rpc("admin_set_system_flag", { _key: key, _enabled: enabled });
    setSaving(null);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: enabled ? "Switch enabled" : "Switch disabled" });
    load();
  };

  const sorted = [...flags].sort((a, b) => ORDER.indexOf(a.key) - ORDER.indexOf(b.key));
  const anyOn = flags.some((f) => f.enabled);

  return (
    <div className="p-6 max-w-3xl">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold">System controls</h1>
        <p className="text-sm text-muted-foreground">Emergency kill-switches. Changes apply instantly across the platform.</p>
      </div>

      {anyOn && (
        <Card className="mb-4 p-3 border-amber-500/40 bg-amber-500/10 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <p className="text-sm">One or more emergency switches are active. User-facing features may be unavailable.</p>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-3">
          {sorted.map((f) => (
            <Card key={f.key} className="p-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Label htmlFor={f.key} className="font-semibold">{LABELS[f.key] ?? f.key}</Label>
                  {f.enabled && <Badge variant="destructive">ACTIVE</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{f.description}</p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Updated {new Date(f.updated_at).toLocaleString()}
                </p>
              </div>
              <Switch
                id={f.key}
                checked={f.enabled}
                disabled={saving === f.key}
                onCheckedChange={(v) => toggle(f.key, v)}
              />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
